import React from 'react';

interface SocialMediaExpirationBannerProps {
  platform: 'linkedin' | 'facebook' | 'instagram' | 'twitter';
  daysUntilExpiry: number;
  onRenew: () => void;
  onDismiss?: () => void;
}

const SocialMediaExpirationBanner: React.FC<SocialMediaExpirationBannerProps> = ({
  platform,
  daysUntilExpiry,
  onRenew,
  onDismiss,
}) => {
  // Determine severity level
  const isUrgent = daysUntilExpiry <= 3;
  const isWarning = daysUntilExpiry > 3 && daysUntilExpiry <= 7;

  // Platform display names
  const platformNames = {
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
  };

  // Platform colors
  const platformColors = {
    linkedin: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    facebook: {
      bg: 'bg-blue-50',
      border: 'border-blue-300',
      text: 'text-blue-900',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    instagram: {
      bg: 'bg-purple-50',
      border: 'border-purple-300',
      text: 'text-purple-900',
      button: 'bg-purple-600 hover:bg-purple-700',
    },
    twitter: {
      bg: 'bg-sky-50',
      border: 'border-sky-300',
      text: 'text-sky-900',
      button: 'bg-sky-600 hover:bg-sky-700',
    },
  };

  const colors = platformColors[platform];

  // Override colors if urgent
  const bgColor = isUrgent ? 'bg-red-50' : colors.bg;
  const borderColor = isUrgent ? 'border-red-300' : colors.border;
  const textColor = isUrgent ? 'text-red-900' : colors.text;
  const buttonColor = isUrgent ? 'bg-red-600 hover:bg-red-700' : colors.button;

  const emoji = isUrgent ? '🔴' : '🟡';
  const platformName = platformNames[platform];

  return (
    <div
      className={`${bgColor} ${borderColor} border-l-4 rounded-lg p-4 mb-4 shadow-sm animate-in fade-in duration-300`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          {/* Emoji Icon */}
          <div className="text-2xl mt-0.5 flex-shrink-0">{emoji}</div>

          {/* Message */}
          <div className="flex-1">
            <h3 className={`font-semibold ${textColor} mb-1`}>
              {platformName} koppeling verloopt binnenkort
            </h3>
            <p className={`text-sm ${textColor} opacity-90`}>
              Je {platformName} koppeling verloopt over{' '}
              <strong>{daysUntilExpiry}</strong> dag{daysUntilExpiry !== 1 ? 'en' : ''}.
              {isUrgent && ' Vernieuw je koppeling nu om te blijven delen!'}
              {!isUrgent && ' Vernieuw je koppeling om automatisch delen te behouden.'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onRenew}
            className={`${buttonColor} text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm hover:shadow`}
          >
            Vernieuwen
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className={`${textColor} opacity-60 hover:opacity-100 transition-opacity p-2`}
              title="Sluiten"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaExpirationBanner;























